<template>
  <!-- Card 86akqxu0h: aviso ao entrar no Connect enquanto houver formulário
       pendente. Não bloqueia: "Agora não" fecha e ele só volta na próxima
       entrada (nova sessão do navegador) ou quando chegar outro formulário. -->
  <v-dialog v-model="open" max-width="520">
    <v-card class="pp-card">
      <div class="pp-icon" :style="{ background: tint }">
        <v-icon size="30" :color="color">mdi-clipboard-text-outline</v-icon>
      </div>
      <div class="pp-title">
        {{ $tc("surveys.weHave", items.length, { count: items.length }) }}
      </div>
      <p class="pp-text">
        {{ $t("surveys.promptText") }}
      </p>

      <div class="pp-list">
        <div v-for="item in items" :key="item.id" class="pp-item">
          <span class="pp-item__title">{{ item.title }}</span>
          <span v-if="item.closesAt" class="pp-item__due">
            {{ $t("surveys.until", { date: item.closesAt.slice(0, 10) }) }}
          </span>
        </div>
      </div>

      <div class="pp-actions">
        <v-btn text @click="dismiss">{{ $t("surveys.notNow") }}</v-btn>
        <v-btn depressed rounded dark :color="color" @click="answer">
          {{ $t("surveys.answerNow") }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { classThemes } from "@/shared/classThemes";
import {
  SURVEYS_REFRESH_EVENT,
  loadPendingSurveys,
  surveyPath,
} from "./surveyEvents";

// Por sessão do navegador: o aviso aparece a cada nova entrada e não a cada
// troca de página.
const SEEN_KEY = "pitch:surveys-prompt-seen";

function readSeen() {
  try {
    return JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeSeen(ids) {
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(ids));
  } catch {
    // Sem sessionStorage o aviso só reaparece mais vezes.
  }
}

export default {
  name: "PendingSurveysPrompt",

  props: {
    profile: {
      type: String,
      default: "customer",
      validator: (value) => ["customer", "partner"].includes(value),
    },
  },

  data() {
    return { items: [], open: false };
  },

  computed: {
    color() {
      return classThemes[this.profile]?.primary || "#A826E1";
    },
    tint() {
      return classThemes[this.profile]?.surfaceAlt || "#f4eafb";
    },
    onSurveyPage() {
      return String(this.$route?.path || "").includes("/formularios/");
    },
  },

  watch: {
    onSurveyPage(value) {
      if (value) this.open = false;
    },
  },

  mounted() {
    this.load();
    window.addEventListener(SURVEYS_REFRESH_EVENT, this.refresh);
  },

  beforeDestroy() {
    window.removeEventListener(SURVEYS_REFRESH_EVENT, this.refresh);
  },

  methods: {
    async load() {
      this.items = await loadPendingSurveys(this.profile);
      const seen = readSeen();
      const unseen = this.items.some((item) => !seen.includes(item.id));
      this.open = unseen && !this.onSurveyPage;
    },

    async refresh() {
      this.items = await loadPendingSurveys(this.profile);
      if (!this.items.length) this.open = false;
    },

    markSeen() {
      writeSeen(
        Array.from(
          new Set([...readSeen(), ...this.items.map((item) => item.id)])
        )
      );
    },

    dismiss() {
      this.markSeen();
      this.open = false;
    },

    answer() {
      this.markSeen();
      this.open = false;
      const [first] = this.items;
      if (first) {
        this.$router.push(surveyPath(this.profile, first.id)).catch(() => null);
      }
    },
  },
};
</script>

<style scoped>
.pp-card {
  padding: 28px 26px 20px;
  border-radius: 16px !important;
  text-align: center;
}

.pp-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pp-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--dk-ink, #2b2540);
}

.pp-text {
  margin: 6px 0 14px;
  color: var(--dk-ink-soft, #4d4760);
}

.pp-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.pp-item {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--dk-paper, #faf9fc);
  border: 1px solid var(--dk-line, #ece8f3);
}

.pp-item__title {
  font-weight: 600;
  color: var(--dk-ink, #2b2540);
}

.pp-item__due {
  color: var(--dk-muted, #6b6580);
  font-size: 0.8rem;
  white-space: nowrap;
}

.pp-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}
</style>
